import { createAsyncThunk } from "@reduxjs/toolkit";
import PaginationParameters from "_shared/_api/queries/pagination/pagination-parameters";
import type { RootState } from "_shared/_stores/redux-store";
import type Guid from "_shared/guids/guid";
import MessageParameters, { MessageFilterParameters } from "messaging/_api/queries/message-parameters";
import MessageQuery from "messaging/_api/queries/message-query";
import type Session from "users/__session/session";
import { populateThreadComments } from "work/proposals/comments/_store/comments-redux-slice";
import { oldestUnreadCommentId } from "work/proposals/comments/oldest-unread-comment";
import type Comment from "work/proposals/comments/comment";
import type CommentThread from "work/proposals/comments/threads/comment-thread";

/** The page a conversation's comments load in: the size the pane's first load uses. */
const pageSize = 100;
/** Enough pages for any conversation a negotiation holds, and a stop for a count the pages never reach. */
const maximumPages = 50;

function threadComments(state: RootState, thread: CommentThread): Comment[] {
  return (state.comments.entries[thread.proposalId.value] ?? []).filter(
    (comment) => comment.thread.id?.value === thread.id?.value,
  );
}

/** Page `pageIndex` of a conversation, counted from its newest comment (#6769). */
function newestFirstPage(pageIndex: number): MessageQuery {
  return new MessageQuery(
    new MessageParameters(new MessageFilterParameters({ newestFirst: true })),
    PaginationParameters.create({ pageIndex, pageSize }),
  );
}

/**
 * Loads older pages of one conversation's comments until every comment the server counts as unread is
 * loaded, or a page comes back short, and answers with the id of the oldest unread one (#5828).
 *
 * The pane loads a conversation's newest page of comments, and page 1 on is each next older page (#6769).
 * The server's count takes in every page, so a conversation with more comments than that has unread
 * ones nobody could scroll to until these arrive.
 */
export const loadCommentsThroughOldestUnread = createAsyncThunk<
  string | undefined,
  { session: Session; thread: CommentThread; readerId: Guid },
  { state: RootState }
>("comments/loadThroughOldestUnread", async ({ session, thread, readerId }, { dispatch, getState }) => {
  const forumId = thread.id?.value;
  if (!forumId) return undefined;

  for (let pageIndex = 1; pageIndex <= maximumPages; pageIndex++) {
    const loaded = threadComments(getState(), thread);
    const loadedUnread = loaded.filter((comment) => comment.isUnreadBy(readerId)).length;
    const serverCount = getState().forumUnreadCounts.entries[forumId] ?? loadedUnread;
    if (loadedUnread >= serverCount) break;

    const page = await dispatch(populateThreadComments({ session, thread, query: newestFirstPage(pageIndex) })).unwrap();
    if (page.length < pageSize) break;
  }
  return oldestUnreadCommentId(threadComments(getState(), thread), readerId);
});

/** What loading a conversation's older comments brought in. */
export type OlderCommentsLoaded = {
  /** How many comments the store did not hold before. */
  added: number;
  /** Whether the conversation's oldest comment is now loaded, so there is nothing older to ask for. */
  reachedOldest: boolean;
};

/**
 * Loads the page of one conversation's comments just older than the ones loaded: what the pane loads
 * when the reader scrolls to the top of the list (#6769). The pane opens on the newest page, so without
 * this a read conversation of more than one page kept its oldest comments out of reach.
 *
 * Counted from the newest comment, the loaded comments are the conversation's first ones, those that
 * arrived since the pane opened included, so the next older comment is on the page their count falls
 * in. A page that brings nothing new, because the store counted a comment the server's list does not
 * hold, moves on to the next one.
 */
export const loadOlderComments = createAsyncThunk<
  OlderCommentsLoaded,
  { session: Session; thread: CommentThread },
  { state: RootState }
>("comments/loadOlder", async ({ session, thread }, { dispatch, getState }) => {
  if (!thread.id) return { added: 0, reachedOldest: true };

  const loadedIds = new Set(threadComments(getState(), thread).map((comment) => comment.id?.value));
  const firstPageIndex = Math.floor(loadedIds.size / pageSize);
  for (let pageIndex = firstPageIndex; pageIndex < firstPageIndex + maximumPages; pageIndex++) {
    const page = await dispatch(populateThreadComments({ session, thread, query: newestFirstPage(pageIndex) })).unwrap();
    const added = page.filter((comment) => !loadedIds.has(comment.id?.value)).length;
    const reachedOldest = page.length < pageSize;
    if (added > 0 || reachedOldest) return { added, reachedOldest };
  }
  return { added: 0, reachedOldest: true };
});
